/**
 * Заметка референса на месте: уход из поля или Enter сохраняет, Esc отменяет.
 * Заметка — наша идея к картинке («что здесь работает, что не повторять»),
 * поэтому она под каждой плиткой, а не в отдельной форме.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useRef, useState } from "react";

import { errorText } from "@/components/trends/ui";
import { saveItemNote, type ReferenceItem } from "@/lib/library";

/** Заметка на месте: blur или Enter — сохранить, Esc — вернуть как было. */
export function NoteEditor({ item }: { item: ReferenceItem }) {
  const queryClient = useQueryClient();
  const saved = item.note ?? "";
  const [value, setValue] = useState(saved);
  const [justSaved, setJustSaved] = useState(false);
  const skipBlur = useRef(false);

  // Свежая заметка с сервиса (после перезапроса) — в поле, если человек её не правит.
  const [seen, setSeen] = useState(saved);
  if (seen !== saved) {
    setSeen(saved);
    setValue(saved);
  }

  const save = useMutation({
    mutationFn: (note: string) => saveItemNote(item.id, note),
    onSuccess: () => {
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 1500);
      void queryClient.invalidateQueries({ queryKey: ["library"] });
      // Карточка референса `/lib/ref/:id` держит заметку в своём кэше.
      void queryClient.invalidateQueries({ queryKey: ["reference"] });
    },
  });

  const commit = () => {
    if (value.trim() === saved.trim()) return;
    save.mutate(value.trim());
  };

  return (
    <div className="grid gap-0.5">
      <div className="relative">
        <textarea
          value={value}
          rows={2}
          maxLength={500}
          placeholder="наша идея: что здесь работает, что не повторять"
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => {
            if (skipBlur.current) {
              skipBlur.current = false;
              return;
            }
            commit();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              e.currentTarget.blur();
            }
            if (e.key === "Escape") {
              // React вешает слушатели на корень: stopPropagation доходит до
              // нативного события и не пускает его к window-слушателю selection.ts.
              e.stopPropagation();
              setValue(saved);
              skipBlur.current = true;
              e.currentTarget.blur();
            }
          }}
          className="block w-full resize-none rounded-[var(--radius-ui)] border border-transparent bg-transparent px-1.5 py-1 text-[12.5px] leading-snug text-fg outline-none placeholder:text-fg-faint hover:border-border focus:border-accent focus:bg-bg"
        />
        {save.isPending || justSaved ? (
          <span className="absolute top-1 right-1 text-[11px] text-fg-faint">
            {save.isPending ? "…" : <Check size={12} className="text-ok" />}
          </span>
        ) : null}
      </div>
      {save.isError ? <div className="text-[11.5px] text-err">Не сохранилось: {errorText(save.error)}</div> : null}
    </div>
  );
}
